<script setup>
import { nextTick, ref, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({ applyLink: { type: Function, required: true } });
const emit = defineEmits(['close']);
const { t } = useI18n();
const fieldId = useId();
const dialog = ref(null);
const input = ref(null);
const href = ref('');
const error = ref('');
const open = async () => {
  href.value = '';
  error.value = '';
  dialog.value.open();
  await nextTick();
  input.value?.focus();
};
// These schemes are rejected, never executed.
// eslint-disable-next-line no-script-url
const blockedProtocols = ['javascript:', 'data:', 'vbscript:'];
const submit = () => {
  const value = href.value.trim();
  try {
    const url = new URL(value, window.location.origin);
    if (!value || blockedProtocols.includes(url.protocol)) {
      throw new Error('invalid');
    }
  } catch {
    error.value = t('EDITOR_TOOLBAR.LINK_DIALOG.INVALID');
    return;
  }
  if (!props.applyLink(value)) {
    error.value = t('EDITOR_TOOLBAR.LINK_DIALOG.SELECTION_CHANGED');
    return;
  }
  dialog.value.close();
};
defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialog"
    :title="t('EDITOR_TOOLBAR.LINK_DIALOG.TITLE')"
    :description="t('EDITOR_TOOLBAR.LINK_DIALOG.DESCRIPTION')"
    :confirm-button-label="t('EDITOR_TOOLBAR.LINK_DIALOG.CONFIRM')"
    @confirm="submit"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-2">
      <label :for="fieldId" class="text-sm font-medium text-n-slate-12">
        {{ t('EDITOR_TOOLBAR.LINK_DIALOG.ADDRESS') }}
      </label>
      <input
        :id="fieldId"
        ref="input"
        v-model="href"
        type="text"
        inputmode="url"
        autocomplete="url"
        aria-required="true"
        :aria-invalid="!!error"
        :aria-describedby="error ? `${fieldId}-error` : undefined"
        :placeholder="t('EDITOR_TOOLBAR.LINK_DIALOG.PLACEHOLDER')"
        class="!mb-0 min-h-11 w-full rounded-xl border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
        @input="error = ''"
      />
      <p
        v-if="error"
        :id="`${fieldId}-error`"
        role="alert"
        class="mb-0 text-sm text-n-ruby-11"
      >
        {{ error }}
      </p>
    </div>
  </Dialog>
</template>
